import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { Product, TestRun, GateResult, LighthouseScore, SecurityScan, Deploy, HealthSnapshot, GateHistoryEntry, DoraProduct, DoraMetrics, EcosystemStats, SecurityOverview, UserProfile, WeightsConfig, NotificationsConfig, DataStats } from '@/lib/types';

export function useProducts() {
  return useQuery({
    queryKey: ['products'],
    queryFn: () => api.get<{ data: Product[] }>('/api/products').then(r => r.data),
  });
}

export function useProduct(slug: string) {
  return useQuery({
    queryKey: ['products', slug],
    queryFn: () => api.get<{ data: Product }>(`/api/products/${slug}`).then(r => r.data),
    enabled: !!slug,
  });
}

export function useTestRuns(slug: string) {
  return useQuery({
    queryKey: ['products', slug, 'test-runs'],
    queryFn: () => api.get<{ data: TestRun[] }>(`/api/products/${slug}/test-runs`).then(r => r.data),
    enabled: !!slug,
  });
}

export function useLatestGates(slug: string) {
  return useQuery({
    queryKey: ['products', slug, 'gates'],
    queryFn: () => api.get<{ data: GateResult[] }>(`/api/products/${slug}/gates`).then(r => r.data),
    enabled: !!slug,
  });
}

export function useLighthouseScores(slug: string) {
  return useQuery({
    queryKey: ['products', slug, 'lighthouse'],
    queryFn: () => api.get<{ data: LighthouseScore[] }>(`/api/products/${slug}/lighthouse`).then(r => r.data),
    enabled: !!slug,
  });
}

export function useSecurityScans(slug: string) {
  return useQuery({
    queryKey: ['products', slug, 'security'],
    queryFn: () => api.get<{ data: SecurityScan[] }>(`/api/products/${slug}/security`).then(r => r.data),
    enabled: !!slug,
  });
}

export function useDeploys(slug: string) {
  return useQuery({
    queryKey: ['products', slug, 'deploys'],
    queryFn: () => api.get<{ data: Deploy[] }>(`/api/products/${slug}/deploys`).then(r => r.data),
    enabled: !!slug,
  });
}

export function useHealthHistory(slug: string) {
  return useQuery({
    queryKey: ['products', slug, 'health-history'],
    queryFn: () => api.get<{ data: HealthSnapshot[] }>(`/api/products/${slug}/health-history`).then(r => r.data),
    enabled: !!slug,
  });
}

export function useGateHistory(slug: string) {
  return useQuery({
    queryKey: ['products', slug, 'gate-history'],
    queryFn: () => api.get<{ data: GateHistoryEntry[] }>(`/api/products/${slug}/gate-history`).then(r => r.data),
    enabled: !!slug,
  });
}

export function useDora(slug: string) {
  return useQuery({
    queryKey: ['dora', slug],
    queryFn: () => api.get<{ data: DoraProduct }>(`/api/dora/${slug}`).then(r => r.data),
    enabled: !!slug,
  });
}

export function useDoraOverview() {
  return useQuery({
    queryKey: ['dora', 'overview'],
    queryFn: () => api.get<{ data: DoraProduct[] }>('/api/dora').then(r => r.data),
  });
}

export function useEcosystemStats() {
  return useQuery({
    queryKey: ['ecosystem', 'stats'],
    queryFn: () => api.get<{ data: EcosystemStats }>('/api/overview/stats').then(r => r.data),
  });
}

export function useSecurityOverview() {
  return useQuery({
    queryKey: ['security', 'overview'],
    queryFn: () => api.get<{ data: SecurityOverview }>('/api/overview/security').then(r => r.data),
  });
}

// ─── Settings Hooks ───

export function useProfile() {
  return useQuery({
    queryKey: ['settings', 'profile'],
    queryFn: () => api.get<{ data: UserProfile }>('/api/settings/profile').then(r => r.data),
  });
}

export function useWeights() {
  return useQuery({
    queryKey: ['settings', 'weights'],
    queryFn: () => api.get<{ data: WeightsConfig }>('/api/settings/weights').then(r => r.data),
  });
}

export function useUpdateWeights() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { weights: WeightsConfig['weights']; thresholds?: WeightsConfig['thresholds'] }) =>
      api.patch<{ data: { message: string } }>('/api/settings/weights', payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['settings', 'weights'] }),
  });
}

export function useNotifications() {
  return useQuery({
    queryKey: ['settings', 'notifications'],
    queryFn: () => api.get<{ data: NotificationsConfig }>('/api/settings/notifications').then(r => r.data),
  });
}

export function useUpdateNotifications() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { slack_webhook_url?: string | null; alerts?: Partial<NotificationsConfig['alerts']> }) =>
      api.patch<{ data: { message: string } }>('/api/settings/notifications', payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['settings', 'notifications'] }),
  });
}

export function useTestSlack() {
  return useMutation({
    mutationFn: () => api.post<{ data: { message: string } }>('/api/settings/notifications/test', {}),
  });
}

export function useDataStats() {
  return useQuery({
    queryKey: ['settings', 'data-stats'],
    queryFn: () => api.get<{ data: DataStats }>('/api/settings/data-stats').then(r => r.data),
  });
}

export function useUpdateProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ slug, ...body }: { slug: string; name?: string; description?: string | null; repo_url?: string | null }) =>
      api.patch<{ data: { message: string } }>(`/api/products/${slug}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['products'] }),
  });
}

export function useDeactivateProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (slug: string) =>
      api.post<{ data: { message: string } }>(`/api/products/${slug}/deactivate`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['products'] }),
  });
}

export function useActivateProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (slug: string) =>
      api.post<{ data: { message: string } }>(`/api/products/${slug}/activate`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['products'] }),
  });
}

export function useRotateKey() {
  return useMutation({
    mutationFn: (slug: string) =>
      api.post<{ data: { api_key: string; message: string } }>(`/api/products/${slug}/rotate-key`, {}),
  });
}

export function usePurgeProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ slug }: { slug: string }) =>
      api.post<{ data: { message: string } }>(`/api/products/${slug}/purge`, { confirm_slug: slug }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['products'] }),
  });
}

export function useDeleteProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ slug }: { slug: string }) =>
      api.del<{ data: { message: string } }>(`/api/products/${slug}`, { confirm_slug: slug }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['products'] }),
  });
}

export function useCreateProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string; slug: string; description?: string; repo_url?: string }) =>
      api.post<{ data: { id: string; name: string; slug: string; api_key: string } }>('/api/products', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['products'] }),
  });
}
